/**
 * The one predicate for "this group has termed" (TERMED-GROUPS.md).
 *
 * Termed is a dated FACT: the CRM account's dts_termdate, read into Vera as
 * `group.termDate`. It is deliberately separate from the vba_isactive archive
 * shelf. Sasha's 9/02 rule: a termed group leaves every DEFAULT list, picker,
 * dashboard and coverage denominator, and stays reachable through search and
 * explicit toggles. This module exists so each surface applies the same test
 * instead of re-deriving it (the 9/02 audit found ~20 surfaces that never
 * consulted the date at all).
 *
 * Comparison runs in LOCAL time, matching the ops lifecycle helper that
 * renders the header badge: a date-only business fact should flip when the
 * person reading it says the day has passed, not at midnight UTC. (The
 * Daylight scorer is the documented exception: a pure module that also runs
 * server-side, it compares against its caller-supplied `today` in UTC.)
 *
 * What this is NOT for: time-aware reporting ("was this group in force during
 * 2025") and per-record resolves (opening a termed group, releasing its form,
 * finance run-out billing) must NOT filter on it - the group still exists.
 */

/** Today as YYYY-MM-DD in local time. */
export function todayLocalYmd(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * True when the term date exists and has passed (or is today). A FUTURE term
 * date is "terming" - still on the book, still in default views: that is the
 * renewal-save window.
 */
export function hasTermed(termDate: string | undefined | null, todayYmd: string = todayLocalYmd()): boolean {
  const term = termDate ? termDate.split('T')[0] : undefined;
  return Boolean(term && term <= todayYmd);
}

/**
 * Filter helper for the default-view rule: keeps everything except groups
 * whose term date has passed. Structural - anything carrying an optional
 * `termDate` qualifies (OpsGroup does).
 */
export function excludeTermed<T extends { termDate?: string | null }>(groups: readonly T[]): T[] {
  const today = todayLocalYmd();
  return groups.filter((g) => !hasTermed(g.termDate ?? undefined, today));
}
